/**
 * MarketingSidebarNav — drop-in renderer for the marketing-mode sidebar.
 *
 * Renders only when the current URL is `/marketing/*`. Shows:
 *   • Mode caption (e.g. "Plan · strategy, targeting, scheduling")
 *   • Section header (PLAN / CREATE / DISTRIBUTE / MEASURE / AGENTS)
 *   • One nav item per page in MARKETING_IA[mode].pages
 *   • Sub-items (indented + left border) under the active parent only
 *
 * Wire it into your existing Sidebar component like so:
 *
 *   const isMarketing = location.pathname.startsWith("/marketing");
 *   return (
 *     <aside className="sidebar">
 *       …header / brand…
 *       <nav className="sidebar-nav">
 *         {isMarketing ? <MarketingSidebarNav /> : <YourCrmNav />}
 *       </nav>
 *       …footer…
 *     </aside>
 *   );
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Users, Mail, PenSquare, Palette, Image,
  Share2, CheckCircle2, Plug, BarChart2, Compass, FileText, Bot, Workflow,
  History, Settings, FolderOpen,
} from "lucide-react";
import { MARKETING_IA, getModeForPath } from "@/lib/marketingIA";

const ICONS = {
  LayoutDashboard, CalendarDays, Users, Mail, PenSquare, Palette, Image,
  Share2, CheckCircle2, Plug, BarChart2, Compass, FileText, Bot, Workflow,
  History, Settings, FolderOpen,
};

function isActivePath(location, path) {
  const [base, query] = path.split("?");
  if (!query) return location.pathname === base;
  if (location.pathname !== base) return false;
  const wanted = new URLSearchParams(query);
  const have = new URLSearchParams(location.search);
  for (const [k, v] of wanted) if (have.get(k) !== v) return false;
  return true;
}

export default function MarketingSidebarNav() {
  const location = useLocation();
  const activeMode = getModeForPath(location.pathname);
  const mode = MARKETING_IA[activeMode] || MARKETING_IA.plan;

  return (
    <>
      <div className="mh-mode-caption" data-testid={`mode-caption-${activeMode}`}>
        <strong>{mode.label}</strong> · {mode.caption}
      </div>
      <div className="sidebar-section">
        <div className="mkt-side-section-label">{mode.label.toUpperCase()}</div>
        {mode.pages.map((page) => {
          const Icon = ICONS[page.icon] || LayoutDashboard;
          const isPageActive = location.pathname === page.path.split("?")[0];
          return (
            <div key={page.id}>
              <Link
                to={page.path}
                className={`sidebar-nav-item ${isPageActive ? "active" : ""}`}
                data-testid={`nav-${page.id}`}
              >
                <Icon style={{ width: 16, height: 16, flexShrink: 0 }} />
                <span>{page.label}</span>
              </Link>
              {isPageActive && page.subItems && (
                <div className="mh-subnav" data-testid={`subnav-${page.id}`}>
                  {page.subItems.map((s) => (
                    <Link
                      key={s.path}
                      to={s.path}
                      className={`mh-subnav-item ${isActivePath(location, s.path) ? "is-active" : ""}`}
                      data-testid={`subnav-${page.id}-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* SYSTEM footer (optional) */}
      <div className="sidebar-section">
        <div className="mkt-side-section-label">SYSTEM</div>
        <Link
          to="/settings"
          className={`sidebar-nav-item ${location.pathname === "/settings" ? "active" : ""}`}
          data-testid="nav-settings"
        >
          <Settings style={{ width: 16, height: 16, flexShrink: 0 }} />
          <span>Settings</span>
        </Link>
      </div>
    </>
  );
}
